import React, { useEffect, useState } from 'react';
import { TestState } from '@/store/InstallationValidation/constants';
import MobileCommonDrawer from '../MobileCommonDrawer/MobileCommonDrawer';
import ButtonRenderer from '../ButtonGroups/ButtonGroups';
import { useTranslation } from 'react-i18next';
import { StyledMainDrawer, StyledNotificationDrawer } from './MobileMainDrawer.styles';
import { DrawerModalState, MobileMainDrawerProps } from './MobileMainDrawer.utils';

const MobileMainDrawer: React.FC<MobileMainDrawerProps> = ({
  visible,
  testState,
  onStartTest,
  onTestAgain,
  onEndTest,
  onCancelTest,
  onSubmitReport,
  isStartingTest,
  isCancelTest,
  isEndingTest,
  isSubmitting,
  testedBy,
  historyTests,
  testDate,
  remainingTimeFormatted,
  numberOfUnresolvedCompressors,
  testId,
  handleUpdateLastTestData,
  handleSubmitClick,
}) => {
  const [modalState, setModalState] = useState<DrawerModalState>({ type: 'none' });
  const [showNotificationDrawer, setShowNotificationDrawer] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (testState === TestState.NEEDS_REVIEW && numberOfUnresolvedCompressors > 0) {
      setModalState({ type: 'unresolved' });
      setShowNotificationDrawer(true);
    } else if (testState === TestState.TEST_SUCCESS) {
      setModalState({ type: 'success' });
      setShowNotificationDrawer(true);
    } else {
      setModalState({ type: 'none' });
      setShowNotificationDrawer(false);
    }
  }, [testState]);

  const handleSubmit = () => {
    onSubmitReport();
    setModalState({ type: 'none' });
    setShowNotificationDrawer(false);
  };

  const handleSubmitButtonClick = () => {
    if (testState === TestState.NEEDS_REVIEW) {
      const isValid = handleSubmitClick();
      if (!isValid) {
        setModalState({ type: 'unresolved' });
        setShowNotificationDrawer(true);
      } else {
        setModalState({
          type: 'confirmation',
          props: {
            onConfirm: () => {
              handleSubmit();
            },
            onCancel: () => {
              setModalState({ type: 'none' });
              setShowNotificationDrawer(false);
            },
          },
        });
        setShowNotificationDrawer(true);
      }
    }
  };

  const renderNotificationDrawer = () => {
    const modalProps = {
      isOpen: showNotificationDrawer && modalState.type !== 'none',
      onClose: () => {
        setModalState({ type: 'none' });
        setShowNotificationDrawer(false);
      },
    };

    switch (modalState.type) {
      case 'unresolved':
        return (
          <StyledNotificationDrawer>
            <MobileCommonDrawer
              {...modalProps}
              type="unresolved"
              message={t('UNRESOLVED_COMPRESSORS_MESSAGE')}
              primaryButton={{
                text: t('BACK_TO_LIST'),
                onClick: () => {
                  setModalState({ type: 'none' });
                  setShowNotificationDrawer(false);
                },
                variant: 'primary',
              }}
              secondaryButton={{
                text: t('TEST_AGAIN'),
                onClick: onTestAgain,
                variant: 'secondary',
              }}
              numberOfUnresolvedCompressors={numberOfUnresolvedCompressors}
            />
          </StyledNotificationDrawer>
        );
      case 'confirmation':
        return (
          <StyledNotificationDrawer>
            <MobileCommonDrawer
              {...modalProps}
              type="confirmation"
              message={t('SUBMIT_CONFIRMATION_MESSAGE')}
              primaryButton={{
                text: t('SUBMIT_REPORT'),
                onClick: modalState.props?.onConfirm,
                variant: 'primary',
              }}
              secondaryButton={{
                text: t('CANCEL'),
                onClick: modalState.props?.onCancel,
                variant: 'secondary',
              }}
            />
          </StyledNotificationDrawer>
        );
      case 'success':
        return (
          <StyledNotificationDrawer>
            <MobileCommonDrawer
              {...modalProps}
              type="success"
              message={t('SUBMIT_CONFIRMATION_MESSAGE')}
              primaryButton={{
                text: t('SUBMIT_REPORT'),
                onClick: handleSubmit,
                variant: 'primary',
              }}
            />
          </StyledNotificationDrawer>
        );
      case 'initError':
        return (
          <StyledNotificationDrawer>
            <MobileCommonDrawer
              {...modalProps}
              type="initError"
              message={t('TEST_NOT_INITIATED_DESC')}
              primaryButton={{
                text: t('BACK_TO_LIST'),
                onClick: () => {
                  setModalState({ type: 'none' });
                  setShowNotificationDrawer(false);
                },
                variant: 'primary',
              }}
              secondaryButton={{
                text: t('TEST_AGAIN'),
                onClick: onTestAgain,
                variant: 'secondary',
              }}
            />
          </StyledNotificationDrawer>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <StyledMainDrawer
        placement="bottom"
        open={visible}
        onClose={() => setModalState({ type: 'none' })}
        closable={false}
        maskClosable={true}
        height="auto"
        mask={false}
        maskStyle={{
          backgroundColor: 'transparent',
          position: 'fixed',
        }}
        className="validation-tool-main-drawer"
      >
        <ButtonRenderer
          historyTests={historyTests}
          testState={testState}
          onStartTest={onStartTest}
          onCancelTest={onCancelTest}
          onTestAgain={onTestAgain}
          onEndTest={onEndTest}
          handleUpdateLastTestData={handleUpdateLastTestData}
          onMobileSubmitReport={handleSubmitButtonClick}
          isStartingTest={isStartingTest}
          isEndingTest={isEndingTest}
          isCancelTest={isCancelTest}
          isSubmitting={isSubmitting}
          isMobile={true}
          testedBy={testedBy}
          testDate={testDate}
          remainingTimeFormatted={remainingTimeFormatted}
          numberOfUnresolvedCompressors={numberOfUnresolvedCompressors}
          testId={testId}
        />
      </StyledMainDrawer>
      {renderNotificationDrawer()}
    </>
  );
};

export default MobileMainDrawer;
